/**
 * Data layer for the Forge builder-v3 Pages editor (ported from
 * growwithhype).
 *
 * The old VV editor drafted per-row `page_blocks` PATCHes; builder-v3
 * pages are whole typed documents (one `_meta` + ordered `section-<n>`
 * json blocks), so the unit of editing here is the PAGE: every edit
 * stages a draft of the full doc and AUTOSAVES it via a debounced PUT
 * `/api/admin/content/page/:slug/sections` (800ms after the last edit to
 * that page). The header shows Saving… / All changes saved / a retry
 * banner — no Save buttons.
 *
 * VALIDATION GATE: the API stores opaque JSON (deliberately — see
 * content.ts), so the editor is the schema boundary. A page that fails
 * PageSchema.parse is NOT sent; the status chip flips to "fix issues" with
 * the Zod paths. Invalid drafts stay local so nothing typed is ever lost.
 *
 * Conflict model: last-writer-wins per page. Two people editing the same
 * page at once is a small-team problem we accept (audit log records both).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PageSchema,
  SECTION_DEFAULTS,
  type PageTemplate,
  type SectionType,
} from '@vv/sections';
import { API_BASE } from './config';
import { authHeaders } from './auth';

const AUTOSAVE_DEBOUNCE_MS = 800;

interface BlockRow {
  id: string;
  page: string;
  block_id: string;
  content_type: string;
  content: string;
  description: string | null;
  display_order: number;
  updated_at: string;
}

export interface SectionDraft {
  /** Stable editor key for React lists + selection. NOT persisted. */
  key: string;
  /**
   * The `section-<n>` block id this section had when LOADED — i.e. its id
   * in the currently-deployed build's DOM (`data-section` attr). Used for
   * preview targeting. `null` for sections added this session (they don't
   * exist in the deployed DOM until the next publish). Kept stable across
   * saves on purpose: saving renumbers D1 rows but not the deployed DOM.
   */
  originalId: string | null;
  /** The section object — `{ type: 'hero', title: … }`. */
  data: Record<string, unknown>;
}

export interface PageMeta {
  title: string;
  description: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: unknown[];
  [key: string]: unknown;
}

export interface PageDoc {
  slug: string;
  meta: PageMeta;
  sections: SectionDraft[];
}

export interface PageIssue {
  path: string;
  message: string;
}

let keyCounter = 0;
function nextKey(): string {
  keyCounter += 1;
  return `s${keyCounter}`;
}

/** Normalize a user-typed slug: lowercase, runs of anything outside
 * [a-z0-9/] become '-', repeated '/' and '-' collapse, and leading/trailing
 * '-' and '/' are stripped (the site's catch-all route can't serve slugs
 * with empty path segments). */
export function sanitizeSlug(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9/]+/g, '-')
    .replace(/\/{2,}/g, '/')
    .replace(/-{2,}/g, '-')
    .replace(/^[-/]+/, '')
    .replace(/[-/]+$/, '');
}

/** Serialize a doc for dirty-comparison + the PUT payload (editor keys and
 * preview ids stripped — only persisted shape matters). */
function docPayload(doc: PageDoc): { meta: Record<string, unknown>; sections: Record<string, unknown>[] } {
  return {
    meta: doc.meta as Record<string, unknown>,
    sections: doc.sections.map((s) => s.data),
  };
}

/** Section payloads for a page template — deep-copied SECTION_DEFAULTS per
 * type, with the template's per-section overrides merged on top. Valid by
 * construction (defaults parse; overrides only touch known string fields). */
export function buildTemplateSections(template: PageTemplate): Record<string, unknown>[] {
  return template.sections.map(({ type, overrides }) => ({
    ...(JSON.parse(JSON.stringify(SECTION_DEFAULTS[type])) as Record<string, unknown>),
    ...(overrides ? (JSON.parse(JSON.stringify(overrides)) as Record<string, unknown>) : {}),
  }));
}

export function usePageDocs() {
  const [docs, setDocs] = useState<Record<string, PageDoc>>({});
  /** JSON snapshots of the last state the server confirmed, keyed by slug. */
  const [savedSnapshots, setSavedSnapshots] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingSlugs, setSavingSlugs] = useState<Set<string>>(new Set());
  const [failedSlugs, setFailedSlugs] = useState<Set<string>>(new Set());
  /** Distinct message for the failed/retry chip (auth expiry, dev-only
   * layout refusals) — the draft stays local on those failures. */
  const [failReason, setFailReason] = useState<string | null>(null);
  /** Per-page Zod issues blocking the save. */
  const [issuesBySlug, setIssuesBySlug] = useState<Record<string, PageIssue[]>>({});
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const timers = useRef<Map<string, number>>(new Map());
  /** Slugs with a PUT on the wire / slugs whose flush arrived while one was.
   * A queued slug gets exactly ONE follow-up flush when the in-flight PUT
   * settles — concurrent PUTs for the same page could land out of order. */
  const inFlight = useRef<Set<string>>(new Set());
  const queuedFlush = useRef<Set<string>>(new Set());
  /** Self-reference so the queued follow-up (and the unmount cleanup) always
   * call the latest flush without widening effect deps. */
  const flushRef = useRef<(slug: string) => Promise<void>>(async () => {});

  // Ref mirrors so flush() reads the latest state synchronously — reading
  // through a setState updater is NOT guaranteed to run when you need it
  // (Forge lesson: v1 of VV's hook never saved anything because of this).
  const docsRef = useRef(docs);
  docsRef.current = docs;
  const savedRef = useRef(savedSnapshots);
  savedRef.current = savedSnapshots;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/content`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`content: HTTP ${res.status}`);
      const data = (await res.json()) as { blocks: BlockRow[] };
      const byPage = new Map<string, BlockRow[]>();
      for (const row of data.blocks) {
        if (!byPage.has(row.page)) byPage.set(row.page, []);
        byPage.get(row.page)!.push(row);
      }
      const nextDocs: Record<string, PageDoc> = {};
      const nextSaved: Record<string, string> = {};
      for (const [slug, rows] of byPage) {
        const metaRow = rows.find((r) => r.block_id === '_meta');
        const sectionRows = rows
          .filter((r) => r.block_id.startsWith('section-') && r.content_type === 'json')
          .sort((a, b) => a.display_order - b.display_order);
        // Pages without the _meta/section structure (the '_global' shared
        // block store, any legacy granular rows) don't belong here.
        if (!metaRow || sectionRows.length === 0) continue;
        let meta: PageMeta;
        try {
          meta = JSON.parse(metaRow.content) as PageMeta;
        } catch {
          continue;
        }
        const sections: SectionDraft[] = [];
        for (const r of sectionRows) {
          try {
            sections.push({
              key: nextKey(),
              originalId: r.block_id,
              data: JSON.parse(r.content) as Record<string, unknown>,
            });
          } catch {
            // A malformed row shouldn't hide the whole page — skip the row.
          }
        }
        const doc: PageDoc = { slug, meta, sections };
        nextDocs[slug] = doc;
        nextSaved[slug] = JSON.stringify(docPayload(doc));
      }
      setDocs(nextDocs);
      setSavedSnapshots(nextSaved);
    } catch (e) {
      setLoadError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = timers.current;
    return () => {
      // Unmount FIRES pending saves instead of dropping them — clearing the
      // timers alone loses anything typed <800ms before navigating away.
      // Fire-and-forget: the PUTs outlive the component; setState no-ops.
      for (const [slug, id] of t) {
        window.clearTimeout(id);
        void flushRef.current(slug);
      }
      t.clear();
    };
  }, [load]);

  /** Validate a doc against PageSchema. Returns issues ([] = valid). */
  const validate = useCallback((doc: PageDoc): PageIssue[] => {
    const candidate = {
      slug: doc.slug,
      ...doc.meta,
      sections: doc.sections.map((s) => s.data),
    };
    const parsed = PageSchema.safeParse(candidate);
    if (parsed.success) return [];
    return parsed.error.issues.map((i) => ({
      path: i.path.join('.'),
      message: i.message,
    }));
  }, []);

  /** The autosave engine. Reads via refs — NEVER via setState updaters. */
  const flush = useCallback(
    async (slug: string) => {
      // Per-slug serialization: while a PUT is in flight, queue exactly one
      // follow-up (it re-reads the latest draft when the PUT settles).
      if (inFlight.current.has(slug)) {
        queuedFlush.current.add(slug);
        return;
      }
      inFlight.current.add(slug);
      try {
        const doc = docsRef.current[slug];
        if (!doc) return;
        const payload = docPayload(doc);
        const payloadJson = JSON.stringify(payload);
        const clearIssues = () =>
          setIssuesBySlug((prev) => {
            if (!prev[slug]) return prev;
            const next = { ...prev };
            delete next[slug];
            return next;
          });
        // Clean — nothing to send. Still clear any "fix issues" / "not saved"
        // flag: a field edited back to its saved value used to leave the
        // chip stuck (retry hit this same early return).
        if (payloadJson === savedRef.current[slug]) {
          clearIssues();
          setFailedSlugs((prev) => {
            if (!prev.has(slug)) return prev;
            const next = new Set(prev);
            next.delete(slug);
            return next;
          });
          return;
        }

        // Validation gate — invalid pages never hit the wire. The draft stays
        // local; the chip shows what to fix.
        const issues = validate(doc);
        if (issues.length > 0) {
          setIssuesBySlug((prev) => ({ ...prev, [slug]: issues }));
          return;
        }
        clearIssues();

        setSavingSlugs((prev) => new Set(prev).add(slug));
        setFailedSlugs((prev) => {
          if (!prev.has(slug)) return prev;
          const next = new Set(prev);
          next.delete(slug);
          return next;
        });
        try {
          const res = await fetch(
            `${API_BASE}/api/admin/content/page/${encodeURIComponent(slug)}/sections`,
            {
              method: 'PUT',
              headers: authHeaders(),
              body: payloadJson,
            },
          );
          if (!res.ok) {
            const body = (await res.json().catch(() => ({}))) as { error?: string };
            // Auth expiry / dev-only layout refusals keep the draft local —
            // say so, or the retry chip reads like lost work.
            setFailReason(
              res.status === 401 || res.status === 403
                ? body.error?.includes('dev-only')
                  ? body.error
                  : 'Session expired — sign in again in a new tab, your edits are safe here until you close this one'
                : null,
            );
            throw new Error(body.error || `HTTP ${res.status}`);
          }
          // Snapshot what we SENT — anything typed mid-flight differs from the
          // snapshot and the next debounce tick flushes it.
          setSavedSnapshots((prev) => ({ ...prev, [slug]: payloadJson }));
          setLastSavedAt(Date.now());
          setFailReason(null);
        } catch {
          setFailedSlugs((prev) => new Set(prev).add(slug));
        } finally {
          setSavingSlugs((prev) => {
            const next = new Set(prev);
            next.delete(slug);
            return next;
          });
        }
      } finally {
        inFlight.current.delete(slug);
        if (queuedFlush.current.delete(slug)) void flushRef.current(slug);
      }
    },
    [validate],
  );
  flushRef.current = flush;

  const scheduleFlush = useCallback(
    (slug: string) => {
      const existing = timers.current.get(slug);
      if (existing) window.clearTimeout(existing);
      timers.current.set(
        slug,
        window.setTimeout(() => {
          timers.current.delete(slug);
          void flush(slug);
        }, AUTOSAVE_DEBOUNCE_MS),
      );
    },
    [flush],
  );

  /** The single write entrypoint — replace a page doc + schedule autosave. */
  const updateDoc = useCallback(
    (slug: string, updater: (doc: PageDoc) => PageDoc) => {
      setDocs((prev) => {
        const cur = prev[slug];
        if (!cur) return prev;
        return { ...prev, [slug]: updater(cur) };
      });
      scheduleFlush(slug);
    },
    [scheduleFlush],
  );

  // ── Edit operations ────────────────────────────────────────────────

  const setSectionData = useCallback(
    (slug: string, key: string, data: Record<string, unknown>) => {
      updateDoc(slug, (doc) => ({
        ...doc,
        sections: doc.sections.map((s) => (s.key === key ? { ...s, data } : s)),
      }));
    },
    [updateDoc],
  );

  const setMeta = useCallback(
    (slug: string, meta: PageMeta) => {
      updateDoc(slug, (doc) => ({ ...doc, meta }));
    },
    [updateDoc],
  );

  const addSection = useCallback(
    (slug: string, type: SectionType, atIndex: number): string => {
      const key = nextKey();
      updateDoc(slug, (doc) => {
        const sections = [...doc.sections];
        sections.splice(Math.max(0, Math.min(atIndex, sections.length)), 0, {
          key,
          originalId: null, // not in the deployed DOM until next publish
          data: JSON.parse(JSON.stringify(SECTION_DEFAULTS[type])) as Record<string, unknown>,
        });
        return { ...doc, sections };
      });
      return key;
    },
    [updateDoc],
  );

  const duplicateSection = useCallback(
    (slug: string, key: string): string | null => {
      const doc = docsRef.current[slug];
      const idx = doc?.sections.findIndex((s) => s.key === key) ?? -1;
      if (!doc || idx < 0) return null;
      const newKey = nextKey();
      updateDoc(slug, (d) => {
        const sections = [...d.sections];
        const src = sections[idx]!;
        sections.splice(idx + 1, 0, {
          key: newKey,
          originalId: null,
          data: JSON.parse(JSON.stringify(src.data)) as Record<string, unknown>,
        });
        return { ...d, sections };
      });
      return newKey;
    },
    [updateDoc],
  );

  const removeSection = useCallback(
    (slug: string, key: string) => {
      updateDoc(slug, (doc) => ({
        ...doc,
        sections: doc.sections.filter((s) => s.key !== key),
      }));
    },
    [updateDoc],
  );

  const moveSection = useCallback(
    (slug: string, key: string, toIndex: number) => {
      updateDoc(slug, (doc) => {
        const sections = [...doc.sections];
        const from = sections.findIndex((s) => s.key === key);
        if (from < 0) return doc;
        const [item] = sections.splice(from, 1);
        sections.splice(Math.max(0, Math.min(toIndex, sections.length)), 0, item!);
        return { ...doc, sections };
      });
    },
    [updateDoc],
  );

  /** Create a brand-new page (saved immediately so it exists server-side).
   * `sections` (template payloads) defaults to a single page-header; when
   * the first section carries a title field, the page title lands there. */
  const createPage = useCallback(
    async (
      slug: string,
      title: string,
      description: string,
      sections?: Record<string, unknown>[],
    ): Promise<{ ok: boolean; error?: string }> => {
      // The PUT is a wipe-and-replace server-side — a colliding slug would
      // silently clobber the existing page, so reject client-side first.
      if (docsRef.current[slug]) {
        return { ok: false, error: 'That slug already exists — open it from the page list instead.' };
      }
      const payloads =
        sections && sections.length > 0
          ? (JSON.parse(JSON.stringify(sections)) as Record<string, unknown>[])
          : [
              JSON.parse(JSON.stringify(SECTION_DEFAULTS['page-header'])) as Record<
                string,
                unknown
              >,
            ];
      // Seed the opener with the page's own title instead of template filler.
      const first = payloads[0]!;
      if (
        (first.type === 'page-header' || first.type === 'hero') &&
        typeof first.title === 'string'
      ) {
        first.title = title;
      }
      const doc: PageDoc = {
        slug,
        meta: { title, description },
        sections: payloads.map((data) => ({ key: nextKey(), originalId: null, data })),
      };
      const payload = docPayload(doc);
      try {
        const res = await fetch(
          `${API_BASE}/api/admin/content/page/${encodeURIComponent(slug)}/sections`,
          { method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload) },
        );
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        setDocs((prev) => ({ ...prev, [slug]: doc }));
        setSavedSnapshots((prev) => ({ ...prev, [slug]: JSON.stringify(payload) }));
        setLastSavedAt(Date.now());
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    [],
  );

  /** Duplicate an existing page under a new slug — server-side clone (one
   * atomic batch), then mirror the doc locally so the editor can open it
   * without a full reload. */
  const clonePage = useCallback(
    async (
      from: string,
      to: string,
      title: string,
      description: string,
    ): Promise<{ ok: boolean; error?: string }> => {
      const source = docsRef.current[from];
      if (!source) return { ok: false, error: 'Source page not loaded' };
      if (docsRef.current[to]) {
        return { ok: false, error: 'That slug already exists — open it from the page list instead.' };
      }
      try {
        const res = await fetch(
          `${API_BASE}/api/admin/content/page/${encodeURIComponent(from)}/clone`,
          {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ to, title, description }),
          },
        );
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        const doc: PageDoc = {
          slug: to,
          meta: { ...source.meta, title, description },
          // originalId null everywhere: the DEPLOYED build has no such page
          // yet, so preview rows correctly show NEW until the next Publish.
          sections: source.sections.map((s) => ({
            key: nextKey(),
            originalId: null,
            data: JSON.parse(JSON.stringify(s.data)) as Record<string, unknown>,
          })),
        };
        setDocs((prev) => ({ ...prev, [to]: doc }));
        setSavedSnapshots((prev) => ({ ...prev, [to]: JSON.stringify(docPayload(doc)) }));
        setLastSavedAt(Date.now());
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    [],
  );

  const deletePage = useCallback(async (slug: string): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/content/page/${encodeURIComponent(slug)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setDocs((prev) => {
        const next = { ...prev };
        delete next[slug];
        return next;
      });
      setSavedSnapshots((prev) => {
        const next = { ...prev };
        delete next[slug];
        return next;
      });
      return true;
    } catch (e) {
      setLoadError((e as Error).message);
      return false;
    }
  }, []);

  /**
   * Re-bind preview ids to the DEPLOYED build's DOM.
   *
   * originalId is guessed at load time from D1 block ids, but saves renumber
   * D1 rows while the deployed build keeps its publish-time ids — after an
   * unpublished layout change plus a reload, the guess is wrong and preview
   * clicks select the wrong section. The bridge reports the deployed DOM's
   * inventory (ids + types, document order) on every iframe load; this
   * aligns the stored sections to it by longest-common-subsequence over the
   * type sequences. Matched sections get the DOM's id; unmatched ones get
   * null (not in the deployed build until the next publish). Touches only
   * originalId — never the persisted payload, so it can't dirty a page.
   */
  const rebindPreviewIds = useCallback(
    (slug: string, deployed: { id: string; type: string }[]) => {
      setDocs((prev) => {
        const doc = prev[slug];
        if (!doc) return prev;
        const a = doc.sections.map((s) => String(s.data.type ?? ''));
        const b = deployed.map((d) => d.type);
        const n = a.length;
        const m = b.length;
        const dp: number[][] = Array.from({ length: n + 1 }, () =>
          new Array<number>(m + 1).fill(0),
        );
        for (let i = n - 1; i >= 0; i--) {
          for (let j = m - 1; j >= 0; j--) {
            dp[i]![j] =
              a[i] === b[j]
                ? dp[i + 1]![j + 1]! + 1
                : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
          }
        }
        // Backtrack, taking every match that lies on an optimal path —
        // leftmost alignment, so equal-typed neighbors pair up in order.
        const assign = new Array<string | null>(n).fill(null);
        let i = 0;
        let j = 0;
        while (i < n && j < m) {
          if (a[i] === b[j] && dp[i]![j] === dp[i + 1]![j + 1]! + 1) {
            assign[i] = deployed[j]!.id;
            i += 1;
            j += 1;
          } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
            i += 1;
          } else {
            j += 1;
          }
        }
        let changed = false;
        const sections = doc.sections.map((s, k) => {
          if (s.originalId === assign[k]) return s;
          changed = true;
          return { ...s, originalId: assign[k] ?? null };
        });
        if (!changed) return prev;
        return { ...prev, [slug]: { ...doc, sections } };
      });
    },
    [],
  );

  /** Re-attempt every failed page. */
  const retryFailed = useCallback(() => {
    for (const slug of failedSlugs) void flush(slug);
  }, [failedSlugs, flush]);

  const dirtySlugs = useMemo(() => {
    const out = new Set<string>();
    for (const [slug, doc] of Object.entries(docs)) {
      if (JSON.stringify(docPayload(doc)) !== savedSnapshots[slug]) out.add(slug);
    }
    return out;
  }, [docs, savedSnapshots]);

  // Closing the tab mid-debounce (or mid-PUT) drops unsaved edits — warn
  // while anything is dirty.
  const hasDirty = dirtySlugs.size > 0;
  useEffect(() => {
    if (!hasDirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasDirty]);

  return {
    docs,
    loading,
    loadError,
    reload: load,
    // edits
    setSectionData,
    setMeta,
    addSection,
    duplicateSection,
    removeSection,
    moveSection,
    createPage,
    clonePage,
    deletePage,
    rebindPreviewIds,
    // save state
    saving: savingSlugs.size > 0,
    failedCount: failedSlugs.size,
    failReason,
    retryFailed,
    issuesBySlug,
    dirtySlugs,
    lastSavedAt,
    validate,
  };
}
